import * as React from "react";

export type PluginToastAction = {
  label: string;
  onClick: () => void;
};

type ToastItem = {
  id: number;
  message: string;
  action?: PluginToastAction;
};

type Listener = (items: ToastItem[]) => void;

let nextId = 1;
let items: ToastItem[] = [];
const listeners = new Set<Listener>();
const timers = new Map<number, number>();
const DURATION_MS = 4200;

function emit() {
  listeners.forEach((fn) => fn([...items]));
}

function dismiss(id: number) {
  const t = timers.get(id);
  if (t != null) {
    window.clearTimeout(t);
    timers.delete(id);
  }
  items = items.filter((i) => i.id !== id);
  emit();
}

/** Bottom-center dark pill toast (clm-redesign WordPlugin style). */
export function showPluginToast(message: string, opts?: { action?: PluginToastAction; durationMs?: number }) {
  const id = nextId++;
  items = [...items.slice(-2), { id, message, action: opts?.action }];
  emit();
  const durationMs = opts?.durationMs ?? DURATION_MS;
  if (durationMs > 0) {
    timers.set(
      id,
      window.setTimeout(() => dismiss(id), durationMs),
    );
  }
  return id;
}

export const PluginToastHost: React.FC = () => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    listeners.add(setToasts);
    setToasts([...items]);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="plugin-toast-host" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className="plugin-toast">
          <span className="plugin-toast__message">{t.message}</span>
          {t.action ? (
            <button
              type="button"
              className="plugin-toast__action"
              onClick={() => {
                t.action?.onClick();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
          <button
            type="button"
            className="plugin-toast__dismiss"
            aria-label="Dismiss"
            onClick={() => dismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};
